<app-screen-shell [contentClass]="'guest-manager-app-shell'" variant="guest" [showHeader]="false">
  @if (guestContinuationClaimed) {
    <section class="guest-continuation-claimed" role="alertdialog" aria-modal="true" [attr.aria-labelledby]="'guestContinuation.claimedTitle' | translate">
      <div class="guest-continuation-claimed-card">
        <span class="guest-continuation-claimed-icon" aria-hidden="true">
          <i [class]="'common.check' | nogoshIcon"></i>
        </span>
        <h1>{{ 'guestContinuation.claimedTitle' | translate }}</h1>
        <p>{{ 'guestContinuation.claimedDescription' | translate }}</p>
        <strong>{{ selectedEvent && selectedEvent.name }}</strong>
        <button type="button" class="guest-continuation-claimed-action" (click)="openClaimedMatchInApp()">
          {{ 'guestContinuation.openInApp' | translate }}
        </button>
      </div>
    </section>
  }
  @if (guestContinuationObserverError && !guestContinuationClaimed) {
    <section class="guest-continuation-observer-error" role="status">
      <span>{{ 'guestContinuation.observerError' | translate }}</span>
      <button type="button" (click)="retryGuestContinuationObserver()">{{ 'guestContinuation.checkAgain' | translate }}</button>
    </section>
  }
  @if (showSetupProgress()) {
    <section class="guest-manager-start-panel" aria-labelledby="guestManagerStartTitle" role="status" aria-live="polite">
      <img class="guest-manager-logo" src="assets/images/footballer-zone-logo-transparent.png" alt="Footballer.zone">
      <app-loading-state titleKey="guestOwner.startingTeam" subtitleKey="guestOwner.startingTeamHint" [compact]="true"></app-loading-state>
      <button type="button" class="guest-manager-text-button" (click)="returnToLanding()">
        {{ 'common.back' | translate }}
      </button>
    </section>
  }

  @if (session && selectedEvent && !editorMode && !guestContinuationClaimed && !restoringGuestContinuation) {
    <section class="guest-manager-phone-frame" [attr.aria-label]="'guestOwner.managerFrame' | translate">
      <app-guest-match-header
        [title]="form.eventName || selectedEvent.name || ('guestOwner.teamName' | translate)"
        [titleClickable]="true"
        [showBack]="true"
        [backLabel]="'publicInvite.changeLanguage' | translate"
        (back)="returnToLanding()"
        (titleClick)="openEditor('name')">
        <button type="button" class="btn btn-outline-primary" [disabled]="loading" (click)="openShareDialog()" data-testid="guest-match-invite">
          <i [class]="'common.share' | nogoshIcon" aria-hidden="true"></i> {{ 'players.invite' | translate }}
        </button>
      </app-guest-match-header>

      <section class="guest-manager-dashboard">
        <app-guest-match-info-grid
          [timeLabel]="'guestOwner.matchTime' | translate"
          [timeValue]="selectedEventDate()"
          [timeDetail]="selectedEventStartTime()"
          [locationLabel]="'guestOwner.matchLocation' | translate"
          [locationValue]="selectedEventLocation()"
          [timeClickable]="true"
          [locationClickable]="true"
          (timeClick)="openEditor('time')"
          (locationClick)="openEditor('location')">
        </app-guest-match-info-grid>

        <section class="guest-manager-add-card" aria-labelledby="guestAddTitle">
          <h2 id="guestAddTitle">{{ 'events.addPlayers' | translate }}</h2>
          <div class="guest-manager-add-row">
            <label class="sr-only" for="guestName">{{ 'guestOwner.playerName' | translate }}</label>
            <input id="guestName" name="guestName" class="guest-manager-input" [(ngModel)]="form.guestName" [placeholder]="'guestOwner.playerName' | translate">
            <button type="button" class="guest-manager-add-button" [disabled]="loading || !(form.guestName || '').trim()" (click)="addGuestPlayer()">
              {{ 'common.add' | translate }}
            </button>
            <button type="button" class="guest-manager-icon-button" [disabled]="loading" [attr.aria-label]="'guestOwner.share.aria' | translate" (click)="openShareDialog()">
              <i [class]="'common.share' | nogoshIcon" aria-hidden="true"></i>
            </button>
          </div>
        </section>

        <app-guest-profile-nudge
          [title]="'guestOwner.profileNudge.title' | translate"
          [description]="'guestOwner.profileNudge.description' | translate"
          [actionLabel]="'guestOwner.createProfile' | translate"
          (action)="openProfileBridge()">
        </app-guest-profile-nudge>

        <app-guest-app-downloads
          [title]="'publicInvite.appDownload.desktopTitle' | translate"
          [description]="'publicInvite.appDownload.desktopInstructions' | translate"
          [openLabel]="'publicInvite.appDownload.continueInApp' | translate"
          [showOpenLink]="true"
          [opening]="openingMobileApp"
          (openRequested)="continueInMobileApp()"
          [androidUrl]="androidDownloadUrl()"
          [iosUrl]="iosDownloadUrl()">
        </app-guest-app-downloads>

        <section class="guest-manager-player-section guest-flow-separated-section">
          <app-guest-participant-section-header
            [title]="'guestOwner.comingPlayers' | translate:{count: comingParticipants().length}"
            [chatLabel]="'guestOwner.chat.title' | translate"
            (chat)="openChatBridge()">
          </app-guest-participant-section-header>
          @if (participantsRealtimeError) {
            <p class="guest-manager-empty">{{ 'guestOwner.participants.reconnecting' | translate }}</p>
          }
          @if (comingParticipants().length === 0) {
            <p class="guest-manager-empty">{{ 'guestOwner.participants.noPlayers' | translate }}</p>
          }
          @if (comingParticipants().length > 0) {
            <app-guest-participant-list
              [items]="comingParticipantItems()"
              [clickable]="true"
              [showChevron]="true"
              (itemClick)="openParticipantItem($event)">
            </app-guest-participant-list>
          }

          @if (notComingParticipants().length > 0) {
          <section class="guest-manager-not-coming-section" aria-labelledby="guestNotComingTitle">
            <h2 id="guestNotComingTitle" class="guest-manager-secondary-heading">
              {{ 'guestOwner.participants.notComingCount' | translate:{count: notComingParticipants().length} }}
            </h2>
            <app-guest-participant-list
              [items]="notComingParticipantItems()"
              [clickable]="true"
              [showChevron]="true"
              (itemClick)="openParticipantItem($event)">
            </app-guest-participant-list>
          </section>
          }
        </section>

        <button type="button" class="guest-manager-delete-match" [disabled]="loading" (click)="deleteMatch()">
          {{ 'guestOwner.deleteTeamAction' | translate }}
        </button>
      </section>
    </section>
  }

  @if (editorMode) {
    <section class="guest-manager-editor" role="dialog" aria-modal="true" [attr.aria-label]="editorTitle()">
      <app-guest-match-header
        [title]="form.eventName || selectedEvent.name || ('guestOwner.teamName' | translate)"
        [showBack]="true"
        [backLabel]="'common.back' | translate"
        (back)="closeEditor()">
      </app-guest-match-header>
      <div class="guest-manager-editor-body">
        <h2>{{ editorTitle() }}</h2>

        @if (editorMode === 'name') {
          <label class="guest-manager-field-label" for="guestEventName">{{ 'guestOwner.teamName' | translate }}</label>
          <input id="guestEventName" name="eventName" class="guest-manager-editor-input" [(ngModel)]="form.eventName" [placeholder]="'guestOwner.enterTeamName' | translate">
          <button type="button" class="guest-manager-link-button" (click)="suggestTeamName()">{{ 'guestOwner.suggestTeamName' | translate }}</button>
        }

        @if (editorMode === 'time') {
          <fieldset class="guest-manager-time-fieldset">
            <legend class="guest-manager-field-label">{{ 'guestOwner.editor.dateTimeLabel' | translate }}</legend>
            <div class="guest-manager-time-grid">
              <label class="guest-manager-time-tile" for="guestEventDate">
                <span>{{ 'events.date' | translate }}</span>
                <input id="guestEventDate"
                  name="startDate"
                  type="date"
                  [(ngModel)]="form.startDate"
                  (ngModelChange)="updateEditorDate($event)"
                  [attr.aria-label]="'events.date' | translate">
              </label>
              <label class="guest-manager-time-tile is-time" for="guestEventClockTime">
                <span>{{ 'events.time' | translate }}</span>
                <input id="guestEventClockTime"
                  name="startTime"
                  type="time"
                  [(ngModel)]="form.startTime"
                  (ngModelChange)="updateEditorTime($event)"
                  [attr.aria-label]="'events.time' | translate">
              </label>
            </div>
          </fieldset>
          @if (hasEditorTime()) {
            <button type="button" class="guest-manager-danger-text" (click)="removeTime()">{{ 'guestOwner.editor.removeTime' | translate }}</button>
          }
        }

        @if (editorMode === 'location') {
          <label class="guest-manager-field-label" for="guestEventLocation">{{ 'common.location' | translate }}</label>
          <input id="guestEventLocation" name="playSpaceName" class="guest-manager-editor-input" [(ngModel)]="form.playSpaceName" [placeholder]="'guestOwner.editor.locationPlaceholder' | translate">
          <div class="guest-manager-location-actions">
            <button type="button" class="guest-manager-location-link" (click)="openProfileBridge('location')">
              <i [class]="'locations.location' | nogoshIcon" aria-hidden="true"></i>
              {{ 'guestOwner.editor.addMapLocation' | translate }}
            </button>
            @if (hasEditorLocation()) {
              <button type="button" class="guest-manager-danger-text" (click)="form.playSpaceName = ''">{{ 'events.locationRemove' | translate }}</button>
            }
          </div>
        }

        @if (editorMode === 'manager') {
          <label class="guest-manager-field-label" for="guestManagerName">{{ 'guestOwner.editor.managerNameLabel' | translate }}</label>
          <input id="guestManagerName" name="managerName" class="guest-manager-editor-input" [(ngModel)]="form.managerName" [placeholder]="'guestOwner.yourName' | translate">
        }

        <button type="button" class="guest-manager-secondary-action" (click)="closeEditor()">{{ 'common.cancel' | translate }}</button>
        <button type="button" class="guest-manager-primary-action" appSaveAction [saving]="loading" [saved]="!!successMsg" (click)="saveEditor()">{{ 'common.save' | translate }}</button>
      </div>
    </section>
  }

  @if (chatBridgeVisible) {
    <div class="guest-manager-sheet-host" role="presentation">
      <button type="button" class="guest-manager-sheet-backdrop" [attr.aria-label]="'common.close' | translate" (click)="closeChatBridge()"></button>
      <section class="guest-manager-sheet is-chat" role="alertdialog" aria-modal="true" aria-labelledby="guestOwnerChatBridgeTitle">
        <button type="button" class="guest-manager-sheet-close" [attr.aria-label]="'common.close' | translate" (click)="closeChatBridge()">
          <i [class]="'common.close' | nogoshIcon" aria-hidden="true"></i>
        </button>
        <div class="guest-manager-sheet-icon" aria-hidden="true">
          <i [class]="'chat.chat' | nogoshIcon" aria-hidden="true"></i>
        </div>
        <h2 id="guestOwnerChatBridgeTitle">{{ 'guestOwner.chat.title' | translate }}</h2>
        <p>{{ 'guestOwner.chat.previewDescription' | translate }}</p>
        <div class="guest-manager-chat-preview" role="log" [attr.aria-label]="'guestOwner.chat.previewTitle' | translate">
          @if (chatMessagesLoading) {
            <div class="guest-manager-chat-state">
              <app-delayed-indicator></app-delayed-indicator>
              {{ 'guestOwner.chat.loading' | translate }}
            </div>
          }
          @if (!chatMessagesLoading && chatMessagesUnavailable) {
            <div class="guest-manager-chat-state">{{ 'guestOwner.chat.unavailable' | translate }}</div>
          }
          @if (!chatMessagesLoading && !chatMessagesUnavailable && chatMessages.length === 0) {
            <div class="guest-manager-chat-state">{{ 'guestOwner.chat.empty' | translate }}</div>
          }
          @if (!chatMessagesLoading && chatMessages.length > 0) {
            @for (message of chatMessages; track message.id) {
              <article class="guest-manager-chat-message">
                <header>
                  <strong>{{ chatMessageSender(message) }}</strong>
                  @if (chatMessageTime(message)) {
                    <small>{{ chatMessageTime(message) }}</small>
                  }
                </header>
                <p>{{ chatMessageText(message) }}</p>
              </article>
            }
          }
        </div>
        <p class="guest-manager-sheet-note">{{ 'guestOwner.chat.composeDescription' | translate }}</p>
        <button type="button" class="guest-manager-primary-action" (click)="createAccount()">{{ 'guestOwner.chat.createAccountCta' | translate }}</button>
      </section>
    </div>
  }

  @if (profileBridgeVisible) {
    <div class="guest-manager-sheet-host" role="presentation">
      <button type="button" class="guest-manager-sheet-backdrop" [attr.aria-label]="'common.close' | translate" (click)="closeProfileBridge()"></button>
      <section class="guest-manager-sheet is-profile" role="alertdialog" aria-modal="true" aria-labelledby="guestOwnerProfileBridgeTitle">
        <div class="guest-manager-sheet-icon is-small" aria-hidden="true">
          <i [class]="'player.add' | nogoshIcon" aria-hidden="true"></i>
        </div>
        <h2 id="guestOwnerProfileBridgeTitle">{{ profileBridgeTitle() }}</h2>
        <p>{{ profileBridgeDescription() }}</p>
        <button type="button" class="guest-manager-secondary-action" (click)="closeProfileBridge()">{{ 'guestOwner.profileDialog.notNow' | translate }}</button>
        <button type="button" class="guest-manager-primary-action" (click)="createAccount()">{{ profileBridgeAction() }}</button>
      </section>
    </div>
  }

  <app-invite-link-sheet *ngIf="shareDialogVisible && inviteUrl" [match]="selectedEvent" [matchName]="selectedEvent?.name" [inviteUrl]="inviteUrl" (closed)="closeShareDialog()"></app-invite-link-sheet>

  @if (selectedParticipant) {
    <div class="guest-manager-sheet-host" role="presentation">
      <button type="button" class="guest-manager-sheet-backdrop" [attr.aria-label]="'common.close' | translate" (click)="closeParticipantSheet()"></button>
      <section class="guest-manager-participant-sheet" role="dialog" aria-modal="true" aria-labelledby="guestOwnerParticipantTitle">
        <header>
          <span class="guest-manager-avatar" aria-hidden="true">{{participantInitial(selectedParticipant)}}</span>
          <div>
            <h2 id="guestOwnerParticipantTitle">{{participantDisplayName(selectedParticipant)}}</h2>
            <p>{{ 'guestOwner.playerDetails' | translate }}</p>
          </div>
          <button type="button" class="guest-manager-sheet-close" [attr.aria-label]="'common.close' | translate" (click)="closeParticipantSheet()">
            <i [class]="'common.close' | nogoshIcon" aria-hidden="true"></i>
          </button>
        </header>
        <section class="guest-manager-response-box">
          <h3>{{ 'players.attendance' | translate }}</h3>
          <div class="guest-manager-response-buttons">
            <button type="button"
              [class.selected]="selectedParticipant.response !== 'NOT_COMING'"
              [disabled]="participantActionInProgress(selectedParticipant)"
              (click)="updateSelectedParticipantResponse('COMING')">
              <i [class]="'common.selected' | nogoshIcon" aria-hidden="true"></i>
              {{ selectedComingActionLabel() }}
            </button>
            <button type="button"
              [class.selected]="selectedParticipant.response === 'NOT_COMING'"
              [disabled]="participantActionInProgress(selectedParticipant)"
              (click)="updateSelectedParticipantResponse('NOT_COMING')">
              <i [class]="'match.status.declined' | nogoshIcon" aria-hidden="true"></i>
              {{ selectedNotComingActionLabel() }}
            </button>
          </div>
        </section>
        @if (!selectedParticipant.matchOwner) {
          <section class="guest-manager-response-box guest-manager-name-box">
            <h3>{{ 'guestOwner.playerName' | translate }}</h3>
            <div class="guest-manager-name-edit-row">
              <label class="sr-only" for="guestParticipantName">{{ 'guestOwner.playerName' | translate }}</label>
              <input id="guestParticipantName" name="selectedParticipantName" class="guest-manager-input" [(ngModel)]="selectedParticipantName" [placeholder]="'guestOwner.playerName' | translate">
              <button type="button"
                class="guest-manager-save-name-button"
                [disabled]="participantActionInProgress(selectedParticipant) || !(selectedParticipantName || '').trim()"
                (click)="saveSelectedParticipantName()">
                {{ 'common.save' | translate }}
              </button>
            </div>
          </section>
        }
        @if (!selectedParticipant.matchOwner) {
          <button type="button" class="guest-manager-danger-outline" [disabled]="participantActionInProgress(selectedParticipant)" (click)="removeSelectedParticipant()">
            {{ 'events.removeFromMatch' | translate }}
          </button>
        }
        @if (selectedParticipant.matchOwner) {
          <button type="button" class="guest-manager-soft-action" (click)="closeParticipantSheet(); openEditor('manager')">
            {{ 'guestOwner.changeManagerName' | translate }}
          </button>
        }
        <button type="button" class="guest-manager-secondary-action" (click)="closeParticipantSheet()">{{ 'common.close' | translate }}</button>
      </section>
    </div>
  }

  @if (confirmDeleteVisible) {
    <div class="guest-manager-sheet-host" role="presentation">
      <button type="button" class="guest-manager-sheet-backdrop" [attr.aria-label]="'common.close' | translate" (click)="cancelDeleteMatch()"></button>
      <section class="guest-manager-sheet is-danger-dialog" role="alertdialog" aria-modal="true" aria-labelledby="guestOwnerDeleteTitle">
        <div class="guest-manager-sheet-icon is-danger" aria-hidden="true">
          <i [class]="'common.delete' | nogoshIcon" aria-hidden="true"></i>
        </div>
        <h2 id="guestOwnerDeleteTitle">{{ 'guestOwner.deleteTeamTitle' | translate }}</h2>
        <p>{{ 'guestOwner.delete.description' | translate }}</p>
        <button type="button" class="guest-manager-secondary-action" (click)="cancelDeleteMatch()">{{ 'common.cancel' | translate }}</button>
        <button type="button" class="guest-manager-danger-action" [disabled]="loading" (click)="confirmDeleteMatch()">{{ 'events.deleteTeam' | translate }}</button>
      </section>
    </div>
  }

  @if (confirmCreateVisible) {
    <div class="guest-manager-sheet-host" role="presentation">
      <button type="button" class="guest-manager-sheet-backdrop" [attr.aria-label]="'common.close' | translate" (click)="cancelCreateMatch()"></button>
      <section class="guest-manager-sheet is-notice-dialog" role="alertdialog" aria-modal="true" aria-labelledby="guestOwnerCreateTitle">
        <div class="guest-manager-sheet-icon is-small" aria-hidden="true">
          <i [class]="'common.info' | nogoshIcon" aria-hidden="true"></i>
        </div>
        <h2 id="guestOwnerCreateTitle">{{ 'guestOwner.createConfirmTitle' | translate }}</h2>
        <p>{{ 'guestOwner.createConfirmMessage' | translate }}</p>
        <button type="button" class="guest-manager-secondary-action" (click)="cancelCreateMatch()">{{ 'common.cancel' | translate }}</button>
        <button type="button" class="guest-manager-primary-action" [disabled]="loading" (click)="confirmCreateMatch()">{{ 'guestOwner.createConfirmAction' | translate }}</button>
      </section>
    </div>
  }

  @if (noticeMessage) {
    <div class="guest-manager-sheet-host" role="presentation">
      <button type="button" class="guest-manager-sheet-backdrop" [attr.aria-label]="'common.close' | translate" (click)="closeNotice()"></button>
      <section class="guest-manager-sheet is-notice-dialog" role="alertdialog" aria-modal="true" aria-labelledby="guestOwnerNoticeTitle">
        <div class="guest-manager-sheet-icon is-small" aria-hidden="true">
          <i [class]="'common.info' | nogoshIcon" aria-hidden="true"></i>
        </div>
        <h2 id="guestOwnerNoticeTitle">{{ noticeTitle }}</h2>
        <p>{{ noticeMessage }}</p>
        <button type="button" class="guest-manager-primary-action" (click)="closeNotice()">{{ 'common.close' | translate }}</button>
      </section>
    </div>
  }
</app-screen-shell>
